import { Map, View } from 'ol';
import * as olProj from 'ol/proj';
import { Attribution, defaults as defaultControls } from 'ol/control';
import LayerGroup from 'ol/layer/Group';
import { defaults as defaultInteractions } from 'ol/interaction';
import { createContext, useRef, useState } from 'react';

const MapContext = createContext({
	inHubGridOpacity: 0.5,
  outHubGridOpacity: 0.5,
  inOutHubGridOpacity: 0.5,
  inflowIndex: 0.035,
  inflowIndexInput: 0.035,
  outflowIndex: 0.3,
  outflowIndexInput: 0.3,
  prevControlOpen: true,
  inLegendControlOpen: true,
  outLegendControlOpen: true,
  inoutLegendControlOpen: true,
  inCollapseBgMap: true,
  outCollapseBgMap: true,
  inoutCollapseBgMap: true,
  inflowBgMap: 'midNight',
  outflowBgMap: 'midNight',
  inoutflowBgMap: 'midNight',
  inPopupOpen: false,
  outPopupOpen: false,
  inOutPopupOpen: false,
  inPopupData: {
		pc_grid_nm: '',
		pc_pop_w: 0,
		pc_pop_r: 0,
		pc_in_val: 0,
		pc_out_val: 0,
	},
  outPopupData: {
		pc_grid_nm: '',
		pc_pop_w: 0,
		pc_pop_r: 0,
		pc_in_val: 0,
		pc_out_val: 0,
	},
  inOutPopupData: {
		pc_grid_nm: '',
		pc_pop_w: 0,
		pc_pop_r: 0,
		pc_in_val: 0,
		pc_out_val: 0,
	},
  inPopupPosition: { x: 0, y: 0 },
  outPopupPosition: { x: 0, y: 0 },
  inOutPopupPosition: { x: 0, y: 0 },
	zoomLevel: 7,
  
  inflowMapRef: null,
  outflowMapRef: null,
  inoutflowMapRef: null
});

const mapView = new View({
	center: olProj.transform([128.0292, 36.6332], 'EPSG:4326', 'EPSG:3857'),
	projection: 'EPSG:3857',
	zoom: 7.3,
	enableRotation: false,
});

const MapProvider = ({ children }) => {
	const [inHubGridOpacity, setInHubGridOpacity] = useState(0.5);
	const [outHubGridOpacity, setOutHubGridOpacity] = useState(0.5);
	const [inOutHubGridOpacity, setInOutHubGridOpacity] = useState(0.5);

	const [inflowIndex, setInflowIndex] = useState(0.035);
	const [inflowIndexInput, setInflowIndexInput] = useState(0.035);

	const [outflowIndex, setOutflowIndex] = useState(0.03);
	const [outflowIndexInput, setOutflowIndexInput] = useState(0.03);

	const [prevControlOpen, setPrevControlOpen] = useState(true);
	const [inLegendControlOpen, setInLegendControlOpen] = useState(true);
	const [outLegendControlOpen, setOutLegendControlOpen] = useState(true);
	const [inoutLegendControlOpen, setInoutLegendControlOpen] = useState(true);

	const [inCollapseBgMap, setInCollapseBgMap] = useState(true);
	const [outCollapseBgMap, setOutCollapseBgMap] = useState(true);
	const [inoutCollapseBgMap, setInoutCollapseBgMap] = useState(true);

	const [inflowBgMap, setInflowBgMap] = useState('midNight');
	const [outflowBgMap, setOutflowBgMap] = useState('midNight');
	const [inoutflowBgMap, setInoutflowBgMap] = useState('midNight');

  const [inPopupOpen, setInPopupOpen] = useState(false);
	const [outPopupOpen, setOutPopupOpen] = useState(false);
	const [inOutPopupOpen, setInOutPopupOpen] = useState(false);
	const [inPopupData, setInPopupData] = useState({
		pc_grid_nm: '',
		pc_pop_w: 0,
		pc_pop_r: 0,
		pc_in_val: 0,
		pc_out_val: 0,
	});
	const [outPopupData, setOutPopupData] = useState({
		pc_grid_nm: '',
		pc_pop_w: 0,
		pc_pop_r: 0,
		pc_in_val: 0,
		pc_out_val: 0,
	});
	const [inOutPopupData, setInOutPopupData] = useState({
		pc_grid_nm: '',
		pc_pop_w: 0,
		pc_pop_r: 0,
		pc_in_val: 0,
		pc_out_val: 0,
	});
	const [inPopupPosition, setInPopupPosition] = useState({ x: 0, y: 0 });
	const [outPopupPosition, setOutPopupPosition] = useState({ x: 0, y: 0 });
	const [inOutPopupPosition, setInOutPopupPosition] = useState({ x: 0, y: 0 });

	const [zoomLevel, setZoomLevel] = useState(7);

  const inflowMapRef = useRef(null);
	const outflowMapRef = useRef(null);
	const inoutflowMapRef = useRef(null);

	const createMap = (id, bgLayers = [], otherLayers = []) => {
		return new Map({
			target: id,
			view: mapView,
			layers: [
				new LayerGroup({
					name: 'bgMap',
					layers: bgLayers,
				}),
				new LayerGroup({
					name: 'otherLayer',
					layers: otherLayers,
				}),
			],
			controls: defaultControls({ zoom: false, attribution: false }).extend([
				new Attribution({ collapsible: false }),
			]),
			interactions: defaultInteractions().extend([]),
		});
	};

	const handleInflowIndexInput = (e) => {
		setInflowIndexInput(Number(e.target.value));
	};

	const handleOutflowIndexInput = (e) => {
		setOutflowIndexInput(Number(e.target.value));
	};


	return (
		<MapContext.Provider
			value={{
				inHubGridOpacity,
        outHubGridOpacity,
        inOutHubGridOpacity,
        inflowIndex,
        inflowIndexInput,
        outflowIndex,
        outflowIndexInput,
        prevControlOpen,
        inLegendControlOpen,
        outLegendControlOpen,
        inoutLegendControlOpen,
        inCollapseBgMap,
        outCollapseBgMap,
        inoutCollapseBgMap,
        inflowBgMap,
        outflowBgMap,
        inoutflowBgMap,
        inPopupOpen,
        outPopupOpen,
        inOutPopupOpen,
        inPopupData,
        outPopupData,
        inOutPopupData,
        inPopupPosition,
        outPopupPosition,
        inOutPopupPosition,
				zoomLevel,
        
        setInHubGridOpacity,
        setOutHubGridOpacity,
        setInOutHubGridOpacity,
        setInflowIndex,
        setInflowIndexInput,
        setOutflowIndex,
        setOutflowIndexInput,
        setPrevControlOpen,
        setInLegendControlOpen,
        setOutLegendControlOpen,
        setInoutLegendControlOpen,
        setInCollapseBgMap,
        setOutCollapseBgMap,
        setInoutCollapseBgMap,
        setInflowBgMap,
        setOutflowBgMap,
        setInoutflowBgMap,
        setInPopupOpen,
        setOutPopupOpen,
        setInOutPopupOpen,
        setInPopupData,
        setOutPopupData,
        setInOutPopupData,
        setInPopupPosition,
        setOutPopupPosition,
        setInOutPopupPosition,
				setZoomLevel,

        createMap,
				handleInflowIndexInput,
				handleOutflowIndexInput,

        inflowMapRef,
        outflowMapRef,
        inoutflowMapRef,
			}}
		>
			{children}
		</MapContext.Provider>
	);
};

export { MapContext, MapProvider };
